import * as THREE from 'three';
import { CAMERA, MAP, NAV, WALK } from '../config.js';

/**
 * 三人称の歩行モード。
 *
 * 当たり判定は2段構え。
 * - 当たり判定マップ（navGrid）が作れた物件 … マス目で壁・家具・部屋の外を判定する
 * - 作れなかった物件（床の無い物体スキャン等） … 従来のレイキャストで代用する
 *
 * レイは全スプラットを走査するため毎フレームは撃てない。
 * 床は一定間隔で探し、その間は前回の高さへ滑らかに寄せる。
 */
export function createWalkMode({ camera, space, avatar, canvas }) {
  const raycaster = new THREE.Raycaster();
  /** 当たり判定マップ。null の間はレイキャストで判定する */
  let nav = null;
  /** ズーム。カメラとキャラの距離に掛ける倍率 */
  let distanceScale = 1;
  /** カメラの置き方。follow = キャラの後ろ / overhead = 上から見下ろす（マップ表示） */
  let view = 'follow';
  let overheadHeight = MAP.heightWide;

  /** キャラの位置と向き。カメラはここから算出する */
  const position = new THREE.Vector3();
  let heading = 0;
  /** カメラの水平回転（キャラの後ろ何度から見るか） */
  let orbit = 0;
  let groundY = WALK.fallbackY;
  let lastProbeAt = 0;
  let enabled = false;
  /** 直近の壁判定。同じ向きに歩いている間は使い回す */
  const wallProbe = { at: 0, dir: null, blocked: false };

  /** キー入力。押されているキーの集合を保持する */
  const keys = new Set();
  /** 画面のバーチャルスティック。-1〜1 */
  const stick = { x: 0, y: 0 };

  const onKeyDown = (e) => {
    if (!enabled) return;
    keys.add(e.code);
    if (MOVE_KEYS.has(e.code)) e.preventDefault();
  };
  const onKeyUp = (e) => keys.delete(e.code);
  const onBlur = () => keys.clear();

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);

  // ドラッグでキャラの周りを回り込む
  let dragging = null;
  const onPointerDown = (e) => {
    if (!enabled) return;
    dragging = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e) => {
    if (!enabled || dragging === null) return;
    orbit -= (e.clientX - dragging.x) * 0.006;
    dragging = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = () => {
    dragging = null;
  };
  canvas.addEventListener('pointerdown', onPointerDown, { passive: true });
  canvas.addEventListener('pointermove', onPointerMove, { passive: true });
  canvas.addEventListener('pointerup', onPointerUp, { passive: true });
  canvas.addEventListener('pointercancel', onPointerUp, { passive: true });

  /** 指定した視点の位置にキャラを立たせる */
  function placeAt(viewpoint) {
    position.fromArray(viewpoint.position);
    const lookAt = new THREE.Vector3().fromArray(viewpoint.lookAt);
    const dir = lookAt.clone().sub(position);
    heading = Math.atan2(dir.x, dir.z);
    orbit = heading;
    groundY = position.y;

    if (nav !== null) {
      // 視点の目線の高さから立つ面の高さを逆算し、その階の一番近い歩ける場所に立たせる
      // （中二階の視点で押したのに真下の1階に降りてしまわないように）
      const standY = viewpoint.position[1] - WALK.viewpointEyeHeight;
      const spot = nav.nearestWalkable(position.x, position.z, standY);
      if (spot !== null) position.set(spot.x, spot.y, spot.z);
      groundY = position.y;
    }
    lastProbeAt = 0;
    wallProbe.at = 0;
    wallProbe.dir = null;
    wallProbe.blocked = false;
    syncAvatar();
    updateCamera(1);
  }

  /**
   * 足元の面が無くなったときに、一番近い歩ける場所へ立たせ直す。
   *
   * 立っていた家具を消すと、その場所に面が無くなる。
   * そのままだと宙に浮いたまま、どの向きにも進めなくなってしまう。
   */
  function reground() {
    if (nav === null) return;
    if (nav.heightAt(position.x, position.z, groundY) !== null) return;
    const spot = nav.nearestWalkable(position.x, position.z, groundY);
    if (spot === null) return;
    position.set(spot.x, spot.y, spot.z);
    groundY = spot.y;
    syncAvatar();
    updateCamera(1);
  }

  function setEnabled(next) {
    enabled = Boolean(next);
    avatar.root.visible = enabled;
    camera.near = enabled ? space.nearClip : CAMERA.near;
    camera.updateProjectionMatrix();
    if (!enabled) keys.clear();
  }

  function setStick(x, y) {
    stick.x = clamp(x, -1, 1);
    stick.y = clamp(y, -1, 1);
  }

  function update(deltaTime) {
    if (!enabled) return { moving: false };

    const input = readInput();
    const moving = input.lengthSq() > 0.0001;

    if (moving) {
      // 入力はカメラの向き基準。画面の「上」が奥になるようにする
      const forward = new THREE.Vector3(Math.sin(orbit), 0, Math.cos(orbit));
      // カメラはキャラの後ろから forward 方向を見ている。画面の右は forward × 上 = (-z, 0, x)
      const right = new THREE.Vector3(-forward.z, 0, forward.x);
      const move = forward
        .multiplyScalar(input.y)
        .add(right.multiplyScalar(input.x))
        .normalize()
        .multiplyScalar(WALK.speed * deltaTime);

      if (nav !== null) moveOnGrid(move);
      else if (!blockedBy(move)) position.add(move);

      /*
       * 進む向きへ少しずつ体を向ける。後ろへ下がるとき（S）も同じで、
       * 後ずさりではなく向きを変えて進む。
       *
       * カメラもキャラの後ろへ回そうとすると、カメラの向きを基準に進む向きを決めているため、
       * 体とカメラが追いかけっこになって回り続ける。カメラは動かさない。
       * 振り向いてカメラに顔が近づいたときは、キャラを薄くして視界をふさがないようにしている。
       */
      heading = lerpAngle(heading, Math.atan2(move.x, move.z), WALK.turnLerp);
    }

    followGround();
    syncAvatar();
    updateCamera(WALK.cameraLerp);
    return { moving, cameraDistance: camera.position.distanceTo(position) };
  }

  /** キーとスティックを合成して -1〜1 の移動入力にする */
  function readInput() {
    let x = stick.x;
    let y = stick.y;
    if (keys.has('KeyW') || keys.has('ArrowUp')) y += 1;
    if (keys.has('KeyS') || keys.has('ArrowDown')) y -= 1;
    if (keys.has('KeyD') || keys.has('ArrowRight')) x += 1;
    if (keys.has('KeyA') || keys.has('ArrowLeft')) x -= 1;
    const v = new THREE.Vector2(clamp(x, -1, 1), clamp(y, -1, 1));
    return v.lengthSq() > 1 ? v.normalize() : v;
  }

  /**
   * 進行方向に面があるか。近すぎるなら進ませない。
   *
   * レイはスプラット数に比例して重いため毎フレームは撃てない。
   * 同じ向きに進んでいる間は前回の判定を使い回し、
   * 向きが変わったか一定時間が過ぎたときだけ測り直す。
   */
  function blockedBy(move) {
    const dir = move.clone().normalize();
    const now = performance.now();

    const stale = now - wallProbe.at >= WALK.wallProbeMs;
    const turned =
      wallProbe.dir === null || wallProbe.dir.angleTo(dir) > WALK.wallProbeAngle;

    if (!stale && !turned) return wallProbe.blocked;

    // 足元ではなく腰の高さから撃つ。床を壁と誤検出しないため
    const origin = position.clone().setY(position.y + 0.35);
    raycaster.set(origin, dir);
    raycaster.near = 0;
    raycaster.far = WALK.wallClearance;
    const hits = [];
    space.raycast(raycaster, hits);

    wallProbe.at = now;
    wallProbe.dir = dir;
    wallProbe.blocked = hits.length > 0;
    return wallProbe.blocked;
  }

  /**
   * マス目の当たり判定で進む。
   * 壁に斜めにぶつかったときに止まってしまわないよう、
   * 進めなければ X だけ・Z だけの移動を順に試して壁沿いに滑らせる。
   */
  function moveOnGrid(move) {
    const candidates = [
      [move.x, move.z],
      [move.x, 0],
      [0, move.z],
    ];
    for (const [dx, dz] of candidates) {
      if (dx === 0 && dz === 0) continue;
      // 今いる面の高さから上り下りできる面があれば進み、その高さを目標にする
      const h = nav.heightAt(position.x + dx, position.z + dz, groundY);
      if (h !== null) {
        position.x += dx;
        position.z += dz;
        groundY = h;
        return;
      }
    }
  }

  /** 一定間隔で真下を探り、床の高さへ滑らかに寄せる */
  function followGround() {
    if (nav !== null) {
      // マップから足元の面の高さが分かっているので、ノイズの多いレイは使わない
      position.y += (groundY - position.y) * WALK.groundLerp;
      return;
    }
    const now = performance.now();
    if (now - lastProbeAt >= WALK.groundProbeMs) {
      lastProbeAt = now;
      const origin = position.clone().setY(position.y + 1.2);
      raycaster.set(origin, DOWN);
      raycaster.near = 0;
      raycaster.far = 4;
      const hits = [];
      space.raycast(raycaster, hits);
      if (hits.length > 0) {
        const nearest = hits.reduce((a, b) => (a.distance <= b.distance ? a : b));
        groundY = nearest.point.y;
      }
    }
    position.y += (groundY - position.y) * WALK.groundLerp;
  }

  function syncAvatar() {
    avatar.root.position.copy(position);
    avatar.root.rotation.y = heading;
  }

  function updateCamera(lerp) {
    const back = new THREE.Vector3(-Math.sin(orbit), 0, -Math.cos(orbit));
    if (view === 'overhead') {
      // 真上から少し手前に倒して見下ろす。画面の上が進む向き（W）になる
      const target = position
        .clone()
        .add(back.multiplyScalar(overheadHeight * MAP.tilt))
        .setY(position.y + overheadHeight);
      camera.position.lerp(target, lerp);
      camera.lookAt(position.x, position.y, position.z);
      return;
    }
    const wanted = WALK.cameraDistance * distanceScale;
    const distance = nav === null ? wanted : clearCameraDistance(back, wanted);
    const height = nav === null ? WALK.cameraHeight : clearCameraHeight(back, distance);
    const target = position
      .clone()
      .add(back.multiplyScalar(distance))
      .setY(position.y + height);

    camera.position.lerp(target, lerp);
    camera.lookAt(position.x, position.y + WALK.lookAtHeight, position.z);
  }

  /**
   * ロフトの下などで頭上が低いとき、カメラを天井の下まで下げる。
   * 下げないとカメラが床板の中に入り、画面が板で埋まって何も見えなくなる。
   */
  function clearCameraHeight(back, distance) {
    if (typeof nav.ceilingAt !== 'function') return WALK.cameraHeight;
    let limit = WALK.cameraHeight;
    const samples = 6;
    for (let i = 0; i <= samples; i += 1) {
      const d = (distance * i) / samples;
      const ceiling = nav.ceilingAt(position.x + back.x * d, position.z + back.z * d, position.y);
      if (ceiling !== null) limit = Math.min(limit, ceiling - position.y - WALK.ceilingMargin);
    }
    return Math.max(limit, WALK.minCameraHeight);
  }

  /**
   * キャラの後ろにカメラを引くとき、壁の向こうや部屋の外に出ないよう距離を縮める。
   * 壁の外は撮影データが無く、そこから覗くと画面が崩れて見えるため。
   */
  function clearCameraDistance(back, wanted) {
    const step = NAV.cell / 2;
    let clear = wanted;
    for (let d = step; d <= wanted; d += step) {
      if (!nav.isCameraOpen(position.x + back.x * d, position.z + back.z * d)) {
        clear = d - step;
        break;
      }
    }
    return Math.max(clear, NAV.minCameraDistance);
  }

  return {
    /** キャラの足元の位置（コピー） */
    get position() {
      return position.clone();
    },
    setEnabled,
    setStick,
    placeAt,
    update,
    /** カメラとキャラの距離の倍率を変える（ズーム） */
    setDistanceScale(scale) {
      distanceScale = clamp(scale, WALK.minDistanceScale, WALK.maxDistanceScale);
    },
    /** カメラの置き方を変える（'follow' | 'overhead'） */
    setView(next) {
      view = next === 'overhead' ? 'overhead' : 'follow';
    },
    /** マップ表示のカメラの高さ(m) */
    setOverheadHeight(height) {
      overheadHeight = height;
    },
    /** 当たり判定マップを差し込む。null でレイキャスト判定に戻る */
    setNav(next) {
      nav = next;
      // 家具を消したあとなど、足元の面が無くなっていたら立ち位置を直す
      if (enabled) reground();
    },
    get nav() {
      return nav;
    },
    get enabled() {
      return enabled;
    },
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
    },
  };
}

const DOWN = Object.freeze(new THREE.Vector3(0, -1, 0));
const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

/** 角度の補間。-π/π をまたいでも一周しないようにする */
function lerpAngle(from, to, t) {
  let diff = ((to - from + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}
